/**
 * Home.jsx
 * -------------
 * Modern rideshare app home screen with contemporary UI design.
 * 
 * Features:
 * • Clean, modern design with minimal styling
 * • Enhanced location input with better UX
 * • Card-based layout with improved visual hierarchy
 * • Quick action buttons with modern styling
 * • Professional color scheme and typography
 *
 * Author: (add your name)
 * Last updated: (yyyy-mm-dd)
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from 'react-router-dom';
import {
  IonContent,
  IonPage,
  IonButton,
  IonInput,
  IonIcon,
  IonAlert,
  IonAvatar
} from '@ionic/react';
import LoadingScreen from '../LoadingScreen/LoadingScreen';
import {
  locationOutline,
  flagOutline,
  carOutline,
  mailOutline,
  timeOutline,
  searchOutline
} from 'ionicons/icons';
import { API_URL } from '../../index';
import './HomeScreen.css';

// For testing error handling, you can use the errorTestingUtils
// Import and call testErrorHandling() in browser console to test various error scenarios

// Configure axios with timeout and better defaults
const apiClient = axios.create({
  timeout: 10000, // 10 second timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Retry configuration
const RETRY_ATTEMPTS = 2;
const RETRY_DELAY = 1000; // 1 second

/**
 * Retry function for failed API calls
 */
const retryApiCall = async (apiCall, retries = RETRY_ATTEMPTS, setRetryState = null) => {
  try {
    if (typeof apiCall !== 'function') {
      throw new Error('apiCall must be a function');
    }
    return await apiCall();
  } catch (error) {
    try {
      if (retries > 0 && (error.code === 'ECONNABORTED' || error.response?.status >= 500)) {
        console.log(`Retrying API call... (${retries} attempts remaining)`);
        if (setRetryState && typeof setRetryState === 'function') {
          setRetryState(true);
        }
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        const result = await retryApiCall(apiCall, retries - 1, setRetryState);
        if (setRetryState && typeof setRetryState === 'function') {
          setRetryState(false);
        }
        return result;
      }
      if (setRetryState && typeof setRetryState === 'function') {
        setRetryState(false);
      }
      throw error;
    } catch (retryError) {
      console.error('Error in retry logic:', retryError);
      if (setRetryState && typeof setRetryState === 'function') {
        setRetryState(false);
      }
      throw error; // Re-throw original error
    }
  }
};

const Home = ({ user }) => {
  const navigate = useNavigate();

  const [displayEndLocation, setDisplayEndLocation] = useState("");
  const [endLocationOptions, setEndLocationOptions] = useState([]);
  const [displayStartLocation, setDisplayStartLocation] = useState("");
  const [startLocationOptions, setStartLocationOptions] = useState([]);

  const [selectedEndLocation, setSelectedEndLocation] = useState("");
  const [selectedStartLocation, setSelectedStartLocation] = useState("");

  const [startResponse, setStartResponse] = useState({});
  const [endResponse, setEndResponse] = useState({});

  // Autocomplete debounce timers
  const [startTimer, setStartTimer] = useState(null)
  const [endTimer, setEndTimer] = useState(null)

  // Dropdown states
  const [isStartDropdownOpen, setIsStartDropdownOpen] = useState(false);
  const [isEndDropdownOpen, setIsEndDropdownOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertConfig, setAlertConfig] = useState({});
  const [isRetrying, setIsRetrying] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.location-input-container')) {
        setIsStartDropdownOpen(false);
        setIsEndDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  /**
   * showErrorAlert
   * Shows an Ionic alert with error details
   */
  const showErrorAlert = (title, message, details = null) => {
    try {
      setAlertConfig({
        header: title,
        message: details ? `${message}\n\n${details}` : message,
        buttons: ['OK']
      });
      setShowAlert(true);
    } catch (error) {
      console.error('Error showing alert:', error);
      // Fallback to console error if alert fails
      console.error(`${title}: ${message}${details ? `\n${details}` : ''}`);
    }
  };

  /**
   * handleApiError
   * Centralized error handling for API calls with proper error categorization
   */
  const handleApiError = (error, operation) => {
    try {
      console.error(`${operation} Error:`, error);

      let title = "Error";
      let message = "An unexpected error occurred";
      let details = null;

      if (error && error.response) {
        // Server responded with error status
        const status = error.response.status;
        const data = error.response.data;

        switch (status) {
        case 400:
          title = "Invalid Request";
          message = "The request was invalid or malformed. Please try again.";
          details = data?.message || data?.error || `Status: ${status}`;
          break;
        case 401:
          title = "Unauthorized";
          message = "You are not authorized to perform this action. Please try again.";
          details = "Please log in again or check your credentials";
          break;
        case 403:
          title = "Forbidden";
          message = "You don't have permission to access this resource. Please try again.";
          break;
        case 404:
          title = "Not Found";
          message = "The requested resource was not found. Please try again.";
          details = `Status: ${status}`;
          break;
        case 429:
          title = "Too Many Requests";
          message = "You've made too many requests. Please try again. later";
          break;
        case 500:
          title = "Server Error";
          message = "The server encountered an internal error. Please try again.";
          details = "Please try again. later or contact support";
          break;
        case 502:
        case 503:
        case 504:
          title = "Service Unavailable";
          message = "The service is temporarily unavailable. Please try again.";
          details = "Please try again. later";
          break;
        default:
          title = "Server Error";
          message = `Server returned error ${status}. Please try again.`;
          details = data?.message || data?.error || `Status: ${status}`;
        }
      } else if (error && error.request) {
        // Request was made but no response received
        title = "Network Error";
        message = "Unable to connect to the server. Please try again.";
        details = "Please check your internet connection and try again.";
      } else if (error && error.code === 'ECONNABORTED') {
        // Request timeout
        title = "Request Timeout";
        message = "The request took too long to complete. Please try again.";
        details = "Please try again. or check your connection.";
      } else if (error && error.name === 'NetworkError') {
        // Network error
        title = "Network Error";
        message = "A network error occurred. Please try again.";
        details = "Please check your connection and try again.";
      } else {
        // Other errors
        title = "Error";
        message = (error && error.message) || "An unexpected error occurred. Please try again.";
      }

      showErrorAlert(title, message, details);
    } catch (alertError) {
      console.error('Error in handleApiError:', alertError);
      // Fallback error handling
      console.error(`${operation} failed:`, error);
    }
  };

  /**
   * validateApiResponse
   * Validates API response data structure
   */
  const validateApiResponse = (response, endpoint) => {
    try {
      if (!response) {
        throw new Error(`No response received from ${endpoint}`);
      }

      if (!response.data) {
        throw new Error(`Invalid response structure from ${endpoint}`);
      }

      // Check if response data is empty or null
      if (response.data === null || response.data === undefined) {
        throw new Error(`Empty response from ${endpoint}`);
      }

      return response.data;
    } catch (error) {
      console.error(`Response validation failed for ${endpoint}:`, error);
      throw error;
    }
  };

  /**
   * handleEmptyResponse
   * Handles cases where API returns empty data
   */
  const handleEmptyResponse = (data, endpoint) => {
    try {
      if (!data || (Array.isArray(data) && data.length === 0)) {
        console.warn(`${endpoint} returned empty data`);
        return [];
      }
      return data;
    } catch (error) {
      console.error(`Error handling empty response for ${endpoint}:`, error);
      return [];
    }
  };

  /**
   * onTypeChangeEndLocation
   * Debounced input handler for END location searchbar.
   */
  const onTypeChangeEndLocation = async (endLocation) => {
    setDisplayEndLocation(endLocation);
    setSelectedEndLocation(endLocation);
    clearTimeout(endTimer);

    // Skip API call if address is empty or whitespace only
    if (!endLocation || !endLocation.trim()) {
      setEndLocationOptions([]);
      setEndResponse([]);
      setIsEndDropdownOpen(false);
      return;
    }

    const newTimer = setTimeout(async () => {
      try {
        const response = await retryApiCall(() =>
          apiClient.post(`${API_URL}/autocomplete`, { query: endLocation })
          , RETRY_ATTEMPTS, setIsRetrying);
        const responseData = validateApiResponse(response, '/autocomplete');

        // Handle new response format with "results" wrapper
        const results = responseData.results || responseData;
        console.info(`Autocomplete (end): received ${results.length} suggestions for "${endLocation}"`);

        let options = results.map(location => {
          return `${location.full_address || ''}`;
        });

        setEndLocationOptions(options);
        setEndResponse(results);

        if (options.length > 0) {
          setIsEndDropdownOpen(true);
        }
      } catch (error) {
        handleApiError(error, 'End Location Autocomplete');
        setEndLocationOptions([]);
        setEndResponse([]);
      }
    }, 300);

    setEndTimer(newTimer);
  };

  /**
   * onTypeChangeStartLocation
   * Debounced input handler for START location searchbar.
   */
  const onTypeChangeStartLocation = async (startLocation) => {
    setDisplayStartLocation(startLocation);
    setSelectedStartLocation(startLocation);
    clearTimeout(startTimer);

    // Skip API call if address is empty or whitespace only
    if (!startLocation || !startLocation.trim()) {
      setStartLocationOptions([]);
      setStartResponse([]);
      setIsStartDropdownOpen(false);
      return;
    }

    const newTimer = setTimeout(async () => {
      try {
        const response = await retryApiCall(() =>
          apiClient.post(`${API_URL}/autocomplete`, { query: startLocation })
          , RETRY_ATTEMPTS, setIsRetrying);
        const responseData = validateApiResponse(response, '/autocomplete');

        // Handle new response format with "results" wrapper
        const results = responseData.results || responseData;
        console.info(`Autocomplete (start): received ${results.length} suggestions for "${startLocation}"`);

        let options = results.map(location => {
          return `${location.full_address || ''}`;
        });

        setStartLocationOptions(options);
        setStartResponse(results);

        if (options.length > 0) {
          setIsStartDropdownOpen(true);
        }
      } catch (error) {
        handleApiError(error, 'Start Location Autocomplete');
        setStartLocationOptions([]);
        setStartResponse([]);
      }
    }, 300);

    setStartTimer(newTimer);
  };

  /**
   * onSelectEndLocation
   * Called when user clicks a suggestion from END dropdown.
   */
  const onSelectEndLocation = (endLocation) => {
    setSelectedEndLocation(endLocation);
    setDisplayEndLocation(endLocation);
    setIsEndDropdownOpen(false);
    console.log(endLocation);
  };

  /**
   * onSelectStartLocation
   * Called when user clicks a suggestion from START dropdown.
   */
  const onSelectStartLocation = (startLocation) => {
    setSelectedStartLocation(startLocation);
    setDisplayStartLocation(startLocation);
    setIsStartDropdownOpen(false);
    console.log(startLocation);
  };

  /**
   * handleStartTrip
   * Validates inputs and navigates to `/starttrip` page with state.
   */
  const handleStartTrip = async () => {
    setIsLoading(true);
    try {
      // Ensure both locations are chosen
      if (!selectedStartLocation || !selectedEndLocation) {
        showErrorAlert("Missing Information", "Please select both start and end locations. Please try again.");
        return;
      }

      // Validate user object
      if (!user || !user.user_id) {
        showErrorAlert("User Error", "User information is missing. Please log in again and try again");
        return;
      }

      // Retrieve the full objects returned by /autocomplete
      const startMatch = Array.isArray(startResponse) ? 
        startResponse.find(loc => loc && loc.full_address === selectedStartLocation) : null;
      const endMatch = Array.isArray(endResponse) ? 
        endResponse.find(loc => loc && loc.full_address === selectedEndLocation) : null;

      if (!startMatch) {
        showErrorAlert("Invalid Start Location", "Please select a valid start location from the suggestions. Please try again.");
        return;
      }
      if (!endMatch) {
        showErrorAlert("Invalid End Location", "Please select a valid end location from the suggestions. Please try again.");
        return;
      }

      // Validate coordinates
      if (!startMatch.longitude || !startMatch.latitude || !endMatch.longitude || !endMatch.latitude) {
        showErrorAlert("Location Error", "Invalid location coordinates. Please try selecting locations again");
        return;
      }

      // Navigate to StartTrip with the coordinates included in state
      navigate("/starttrip", {
        state: {
          user,
          startLocation: selectedStartLocation,
          endLocation: selectedEndLocation,
          startlongitude: startMatch.longitude,
          startLatitude: startMatch.latitude,
          endLongitude: endMatch.longitude,
          endLatitude: endMatch.latitude,
        },
      });
    } catch (error) {
      console.error("Error starting trip:", error);
      handleApiError(error, 'Starting Trip');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * handlePastTrips
   * Fetches rider & driver past trips concurrently then navigates.
   */
  const handlePastTrips = async () => {
    setIsLoading(true);

    console.log(user);
    try {
      // Create the request object with UserID, Year, and Month (using current date)
      const currentDate = new Date();
      const requestData = {
        UserID: user.user_id,
        Year: currentDate.getFullYear(),
        Month: currentDate.getMonth() + 1  // getMonth() returns 0-11, so add 1
      };

      const [riderResponse, driverResponse] = await Promise.allSettled([
        apiClient.post(`${API_URL}/pasttripsgeneraltorider`, requestData),
        apiClient.post(`${API_URL}/pasttripsgeneraltodriver`, requestData)
      ]);

      // Check if both requests succeeded
      if (riderResponse.status === 'rejected' && driverResponse.status === 'rejected') {
        throw new Error('Both rider and driver past trips failed to load. Please try again.');
      }

      // Handle rider response
      let riderData = [];
      if (riderResponse.status === 'fulfilled') {
        riderData = validateApiResponse(riderResponse.value, '/pasttripsgeneraltorider');
        riderData = handleEmptyResponse(riderData, '/pasttripsgeneraltorider');
      } else {
        console.warn('Rider past trips failed:', riderResponse.reason);
        showErrorAlert("Partial Data", "Rider past trips failed to load. Please try again.", "Driver data loaded successfully");
      }

      // Handle driver response
      let driverData = [];
      if (driverResponse.status === 'fulfilled') {
        driverData = validateApiResponse(driverResponse.value, '/pasttripsgeneraltodriver');
        driverData = handleEmptyResponse(driverData, '/pasttripsgeneraltodriver');
      } else {
        console.warn('Driver past trips failed:', driverResponse.reason);
        if (riderResponse.status === 'fulfilled') {
          showErrorAlert("Partial Data", "Driver past trips failed to load. Please try again.", "Rider data loaded successfully");
        }
      }

      console.info(`Past trips fetched — rider: ${Array.isArray(riderData) ? riderData.length : 0}, driver: ${Array.isArray(driverData) ? driverData.length : 0}`);

      navigate("/pasttrips", {
        state: {
          user: user,
          pastTripResultsRider: riderData,
          pastTripResultsDriver: driverData
        }
      });
    } catch (error) {
      handleApiError(error, 'Fetching Past Trips');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * handleUpcomingTrips
   * Fetches upcoming trips and routes to list screen.
   */
  const handleUpcomingTrips = async () => {
    setIsLoading(true);
    try {
      const [riderResponse, driverResponse] = await Promise.allSettled([
        apiClient.post(`${API_URL}/upcomingtripsgeneraltorider`, user),
        apiClient.post(`${API_URL}/upcomingtripsgeneraltodriver`, user)
      ]);

      // Check if both requests succeeded
      if (riderResponse.status === 'rejected' && driverResponse.status === 'rejected') {
        throw new Error('Both rider and driver upcoming trips failed to load. Please try again.');
      }

      // Handle rider response
      let riderData = [];
      if (riderResponse.status === 'fulfilled') {
        riderData = validateApiResponse(riderResponse.value, '/upcomingtripsgeneraltorider');
        riderData = handleEmptyResponse(riderData, '/upcomingtripsgeneraltorider');
      } else {
        console.warn('Rider upcoming trips failed:', riderResponse.reason);
        showErrorAlert("Partial Data", "Rider upcoming trips failed to load. Please try again.", "Driver data loaded successfully");
      }

      // Handle driver response
      let driverData = [];
      if (driverResponse.status === 'fulfilled') {
        driverData = validateApiResponse(driverResponse.value, '/upcomingtripsgeneraltodriver');
        driverData = handleEmptyResponse(driverData, '/upcomingtripsgeneraltodriver');
      } else {
        console.warn('Driver upcoming trips failed:', driverResponse.reason);
        if (riderResponse.status === 'fulfilled') {
          showErrorAlert("Partial Data", "Driver upcoming trips failed to load. Please try again.", "Rider data loaded successfully");
        }
      }

      console.info(`Upcoming trips fetched — rider: ${Array.isArray(riderData) ? riderData.length : 0}, driver: ${Array.isArray(driverData) ? driverData.length : 0}`);

      navigate("/upcomingtrips", {
        state: {
          user: user,
          upcomingTripResultsRider: riderData,
          upcomingTripResultsDriver: driverData
        }
      });
    } catch (error) {
      handleApiError(error, 'Fetching Upcoming Trips');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * handlePendingTrips
   * Fetches pending trip requests and routes to list screen.
   */
  const handlePendingTrips = async () => {
    setIsLoading(true);
    try {
      const [riderResponse, driverResponse] = await Promise.allSettled([
        apiClient.post(`${API_URL}/pendingtripsgeneraltorider`, user),
        apiClient.post(`${API_URL}/pendingtripsgeneraltodriver`, user)
      ]);

      // Check if both requests succeeded
      if (riderResponse.status === 'rejected' && driverResponse.status === 'rejected') {
        throw new Error('Both rider and driver pending trips failed to load. Please try again.');
      }

      // Handle rider response
      let riderData = [];
      if (riderResponse.status === 'fulfilled') {
        riderData = validateApiResponse(riderResponse.value, '/pendingtripsgeneraltorider');
        riderData = handleEmptyResponse(riderData, '/pendingtripsgeneraltorider');
      } else {
        console.warn('Rider pending trips failed:', riderResponse.reason);
        showErrorAlert("Partial Data", "Rider pending trips failed to load. Please try again.", "Driver data loaded successfully");
      }

      // Handle driver response
      let driverData = [];
      if (driverResponse.status === 'fulfilled') {
        driverData = validateApiResponse(driverResponse.value, '/pendingtripsgeneraltodriver');
        driverData = handleEmptyResponse(driverData, '/pendingtripsgeneraltodriver');
      } else {
        console.warn('Driver pending trips failed:', driverResponse.reason);
        if (riderResponse.status === 'fulfilled') {
          showErrorAlert("Partial Data", "Driver pending trips failed to load. Please try again.", "Rider data loaded successfully");
        }
      }

      console.info(`Pending trips fetched — rider: ${Array.isArray(riderData) ? riderData.length : 0}, driver: ${Array.isArray(driverData) ? driverData.length : 0}`);

      navigate("/pendingtrips", {
        state: {
          user: user,
          pendingTripResultsRider: riderData,
          pendingTripResultsDriver: driverData
        }
      });
    } catch (error) {
      handleApiError(error, 'Fetching Pending Trips');
    } finally {
      setIsLoading(false);
    }
  };

  const navigateToProfile = () => {
    try {
      navigate("/profile");
    } catch (error) {
      console.error("Error navigating to profile:", error);
      handleApiError(error, 'Navigation to Profile');
    }
  };

  return (
    <IonPage className="home-page">
      <IonContent fullscreen className="home-content" scrollY={true}>
        <div className="top-padding"></div>
        <div className="content-container">
          {/* User Greeting and Profile */}
          <div className="user-header">
            <div className="user-greeting">
              <h2 className="greeting-text">Hello, {user.given_name}!</h2>
              <p className="greeting-subtitle">Where are you going today?</p>
            </div>
            <IonButton
              fill="clear"
              onClick={navigateToProfile}
              className="profile-button"
            >
              <IonAvatar className="profile-avatar">
                <div className="avatar-placeholder">
                  {user.given_name?.[0]?.toUpperCase()}
                </div>
              </IonAvatar>
            </IonButton>
          </div>

          {/* Main Search Card */}
          <div className="search-card">
            <div className="search-header">
              <h3>Plan your trip</h3>
              <p>Choose your pickup and destination</p>
            </div>

            <div className="location-inputs">
              {/* Start Location */}
              <div className="location-input-wrapper">
                <div className="location-input-container">
                  <div className="location-dot start-dot"></div>
                  <div className="input-content">
                    <IonInput
                      value={displayStartLocation}
                      placeholder="Pick-up location"
                      onIonInput={(e) => onTypeChangeStartLocation(e.detail.value)}
                      onIonFocus={() => startLocationOptions.length > 0 && setIsStartDropdownOpen(true)}
                      className="modern-location-input"
                    />
                  </div>
                </div>

                {isStartDropdownOpen && startLocationOptions.length > 0 && (
                  <div className="location-dropdown modern-dropdown">
                    {startLocationOptions.map((option, index) => (
                      <div
                        key={index}
                        className="dropdown-option"
                        onClick={() => onSelectStartLocation(option)}
                      >
                        <IonIcon icon={locationOutline} className="option-icon" />
                        <span className="option-text">{option}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Connection Line */}
              <div className="connection-line"></div>

              {/* End Location */}
              <div className="location-input-wrapper">
                <div className="location-input-container">
                  <div className="location-dot end-dot"></div>
                  <div className="input-content">
                    <IonInput
                      value={displayEndLocation}
                      placeholder="Drop-off location"
                      onIonInput={(e) => onTypeChangeEndLocation(e.detail.value)}
                      onIonFocus={() => endLocationOptions.length > 0 && setIsEndDropdownOpen(true)}
                      className="modern-location-input"
                    />
                  </div>
                </div>

                {isEndDropdownOpen && endLocationOptions.length > 0 && (
                  <div className="location-dropdown modern-dropdown">
                    {endLocationOptions.map((option, index) => (
                      <div
                        key={index}
                        className="dropdown-option"
                        onClick={() => onSelectEndLocation(option)}
                      >
                        <IonIcon icon={flagOutline} className="option-icon" />
                        <span className="option-text">{option}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <IonButton
              expand="block"
              onClick={handleStartTrip}
              className="search-trip-button"
              disabled={!selectedStartLocation || !selectedEndLocation}
            >
              <IonIcon icon={searchOutline} slot="start" />
              Start your trip
            </IonButton>
          </div>

          {/* Quick Actions Grid */}
          <div className="quick-actions">
            <h4 className="section-title">Quick actions</h4>
            <div className="action-grid">
              <div className="action-card" onClick={handlePendingTrips}>
                <div className="action-icon pending-icon">
                  <IonIcon icon={mailOutline} />
                </div>
                <div className="action-content">
                  <h5>Pending</h5>
                  <p>Requests waiting</p>
                </div>
              </div>

              <div className="action-card" onClick={handleUpcomingTrips}>
                <div className="action-icon upcoming-icon">
                  <IonIcon icon={carOutline} />
                </div>
                <div className="action-content">
                  <h5>Upcoming</h5>
                  <p>Scheduled trips</p>
                </div>
              </div>

              <div className="action-card" onClick={handlePastTrips}>
                <div className="action-icon history-icon">
                  <IonIcon icon={timeOutline} />
                </div>
                <div className="action-content">
                  <h5>History</h5>
                  <p>Past trips</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {isLoading && <LoadingScreen />}

        {isRetrying && <LoadingScreen />}

        <IonAlert
          isOpen={showAlert}
          onDidDismiss={() => {
            try {
              setShowAlert(false);
            } catch (error) {
              console.error('Error dismissing alert:', error);
            }
          }}
          header={alertConfig.header || 'Error'}
          message={alertConfig.message || 'An error occurred'}
          buttons={alertConfig.buttons || ['OK']}
          backdropDismiss={true}
          keyboardClose={true}
        />
      </IonContent>
    </IonPage>
  );
};

export default Home;